import { registerSW } from 'virtual:pwa-register'
import { DEBUG, DEBUG_MORE, IS_TEST } from '../config'
import { getActions } from '../global'
import { formatShareText } from './deeplink'
import { IS_ANDROID, IS_IOS, IS_SERVICE_WORKER_SUPPORTED } from './windowEnvironment'
import { validateFiles } from './files'
import { notifyClientReady, playNotifySoundDebounced } from './notifications'
import { pause } from './schedulers'

type WorkerAction = {
  type: string
  payload: Record<string, any>
}

const CONTROLLER_WAIT_TIMEOUT = 3000

function handleWorkerMessage(e: MessageEvent) {
  const action: WorkerAction = e.data
  if (DEBUG_MORE) {
    // eslint-disable-next-line no-console
    console.log('[SW] Message from worker', action)
  }
  if (!action.type) {
    return
  }
  const dispatch = getActions()
  const payload = action.payload
  switch (action.type) {
    case 'focusMessage':
      if (dispatch.focusMessage) {
        dispatch.focusMessage(payload as any)
      }
      break
    case 'playNotificationSound':
      playNotifySoundDebounced(action.payload.id)
      break
    case 'share':
      dispatch.openChatWithDraft({
        text: formatShareText(payload.url, payload.text, payload.title),
        files: validateFiles(payload.files),
      })
      break
  }
}

function subscribeToWorker() {
  navigator.serviceWorker.removeEventListener('message', handleWorkerMessage)
  navigator.serviceWorker.addEventListener('message', handleWorkerMessage)
  // Notify web worker that client is ready to receive messages
  notifyClientReady()
}

function registerServiceWorker() {
  registerSW({
    immediate: true,
    onRegisteredSW() {
      if (DEBUG) {
        // eslint-disable-next-line no-console
        console.log('[SW] ServiceWorker registered')
      }
    },
    onRegisterError(err) {
      if (DEBUG) {
        // eslint-disable-next-line no-console
        console.error('[SW] ServiceWorker registration failed: ', err)
      }
    },
  })
}

async function waitForController() {
  if (navigator.serviceWorker.controller) {
    return true
  }

  await Promise.race([
    new Promise<void>((resolve) => {
      navigator.serviceWorker.addEventListener('controllerchange', () => resolve(), { once: true })
    }),
    pause(CONTROLLER_WAIT_TIMEOUT),
  ])

  return navigator.serviceWorker.controller
}

if (IS_SERVICE_WORKER_SUPPORTED) {
  window.addEventListener('load', async () => {
    try {
      if (!navigator.serviceWorker.controller) {
        const registrations = await navigator.serviceWorker.getRegistrations()
        if (registrations.length) {
          if (DEBUG) {
            // eslint-disable-next-line no-console
            console.log('[SW] Hard reload detected, re-enabling Service Worker')
          }
          await Promise.all(registrations.map((r) => r.unregister()))
        }
      }

      registerServiceWorker()

      await navigator.serviceWorker.ready

      if (await waitForController()) {
        if (DEBUG) {
          // eslint-disable-next-line no-console
          console.log('[SW] ServiceWorker ready')
        }
        subscribeToWorker()
      } else {
        if (DEBUG) {
          // eslint-disable-next-line no-console
          console.error('[SW] ServiceWorker not available')
        }

        if (!IS_IOS && !IS_ANDROID && !IS_TEST) {
          getActions().showDialog?.({ data: { message: 'SERVICE_WORKER_DISABLED', hasErrorKey: true } })
        }
      }
    } catch (err) {
      if (DEBUG) {
        // eslint-disable-next-line no-console
        console.error('[SW] ServiceWorker registration failed: ', err)
      }
    }
  })
  window.addEventListener('focus', async () => {
    await navigator.serviceWorker.ready
    subscribeToWorker()
  })
}
